/**
 * <google-reviews> — combined Google reviews feed for both BBQ Adelaide stores.
 *
 * Reads its config from data-attributes (set from the section schema in Liquid).
 * - SAMPLE-DATA MODE: when data-use-sample="true" OR no feed id is set, renders a
 *   built-in believable SAMPLE array so the section always looks complete.
 * - LIVE MODE: fetches each Featurable widget (one per Google Business Profile)
 *   client-side with Promise.allSettled, tags each review with its store label,
 *   concats, filters by min rating, sorts newest-first, dedupes, slices.
 *   On any fetch failure it console.warns and falls back to sample data so the
 *   homepage never shows a broken/empty section.
 *
 * Vanilla custom element, no deps, re-inits on shopify:section:load.
 */
(function () {
  'use strict';

  // Literal hexes by design: JS can't read the CSS --weber-red token cheaply,
  // and these only color reviewer-initial avatars (first entry = brand red).
  var AVATAR_COLORS = [
    '#EF2D2D', '#1f6f4a', '#2f6db5', '#b5651d',
    '#7a4fb5', '#0f8a8a', '#c2185b', '#5a6b2f'
  ];

  // Believable BBQ Adelaide sample reviews. Alternating Gepps Cross / Mile End.
  function sampleReviews(label1, label2) {
    var now = Date.now();
    var day = 86400000;
    return [
      {
        reviewId: 'sample-1',
        name: 'Daniel R.',
        photo: '',
        rating: 5,
        comment: 'Picked up a Weber Genesis and the team walked me through every setting. Best BBQ advice in Adelaide — burnt ends sorted for the weekend.',
        createTime: new Date(now - 3 * day).toISOString(),
        location: label1
      },
      {
        reviewId: 'sample-2',
        name: 'Megan T.',
        photo: '',
        rating: 5,
        comment: 'Grabbed a YETI Tundra 45 and a couple of their house rubs. Staff knew their gear inside out and didn\'t rush me at all.',
        createTime: new Date(now - 9 * day).toISOString(),
        location: label2
      },
      {
        reviewId: 'sample-3',
        name: 'Chris P.',
        photo: '',
        rating: 5,
        comment: 'Assembled my Weber Kettle and threw in a chimney starter. Genuine experts who actually cook on this stuff.',
        createTime: new Date(now - 16 * day).toISOString(),
        location: label1
      },
      {
        reviewId: 'sample-4',
        name: 'Sarah L.',
        photo: '',
        rating: 4,
        comment: 'Great range of YETI drinkware and the spice rubs are excellent. Only knocked a star because my colour was out of stock — back in next week though.',
        createTime: new Date(now - 24 * day).toISOString(),
        location: label2
      },
      {
        reviewId: 'sample-5',
        name: 'Anthony M.',
        photo: '',
        rating: 5,
        comment: 'These guys sorted a warranty part for my old Weber no questions asked. Loyal customer for life. Smoked brisket has never been better.',
        createTime: new Date(now - 38 * day).toISOString(),
        location: label1
      },
      {
        reviewId: 'sample-6',
        name: 'Priya K.',
        photo: '',
        rating: 5,
        comment: 'Upgraded to a Weber Q for the balcony and the in-store demo sealed it. Friendly, no pressure, proper local BBQ knowledge.',
        createTime: new Date(now - 52 * day).toISOString(),
        location: label2
      }
    ];
  }

  function num(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  function initials(name) {
    if (!name) return 'G';
    var parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'G';
    var first = parts[0].charAt(0);
    var last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
  }

  function avatarColor(name) {
    var key = name || 'G';
    var sum = 0;
    for (var i = 0; i < key.length; i++) sum += key.charCodeAt(i);
    return AVATAR_COLORS[sum % AVATAR_COLORS.length];
  }

  function relativeDate(iso) {
    var t = Date.parse(iso);
    if (isNaN(t)) return '';
    var diff = Date.now() - t;
    if (diff < 0) diff = 0;
    var day = 86400000;
    var days = Math.floor(diff / day);
    if (days <= 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return days + ' days ago';
    if (days < 14) return 'a week ago';
    if (days < 31) return Math.floor(days / 7) + ' weeks ago';
    if (days < 60) return 'a month ago';
    if (days < 365) return Math.floor(days / 30) + ' months ago';
    if (days < 730) return 'a year ago';
    return Math.floor(days / 365) + ' years ago';
  }

  // Normalise one raw Featurable review object into our internal shape.
  function normalise(raw, label) {
    if (!raw || typeof raw !== 'object') return null;
    var reviewer = raw.reviewer || {};
    var rating = num(raw.starRating, 0);
    if (!rating || rating < 1) return null;
    var name = reviewer.isAnonymous ? 'Google user' : (reviewer.displayName || 'Google user');
    return {
      reviewId: raw.reviewId || (label + '-' + Math.random().toString(36).slice(2)),
      name: name,
      photo: reviewer.profilePhotoUrl || '',
      rating: Math.min(5, Math.max(1, rating)),
      comment: typeof raw.comment === 'string' ? raw.comment : '',
      createTime: raw.createTime || '',
      location: label
    };
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function starsMarkup(rating) {
    var html = '<div class="google-reviews__stars" aria-label="' + rating + ' out of 5 stars">';
    for (var i = 1; i <= 5; i++) {
      var filled = i <= rating ? ' is-filled' : '';
      html += '<svg class="google-reviews__star' + filled + '" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
        '<path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.78L10 14.77 4.8 17.5l.99-5.78L1.58 7.62l5.82-.85z"/></svg>';
    }
    html += '</div>';
    return html;
  }

  function googleGlyph() {
    return '<svg class="google-reviews__g" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path fill="#4285F4" d="M22.5 12.2c0-.74-.07-1.45-.19-2.13H12v4.03h5.9a5.05 5.05 0 0 1-2.19 3.31v2.75h3.54c2.07-1.91 3.25-4.72 3.25-7.96z"/>' +
      '<path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.54-2.75c-.98.66-2.24 1.05-3.74 1.05-2.87 0-5.3-1.94-6.17-4.55H2.18v2.84A11 11 0 0 0 12 23z"/>' +
      '<path fill="#FBBC05" d="M5.83 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86z"/>' +
      '<path fill="#EA4335" d="M12 4.75c1.62 0 3.07.56 4.21 1.65l3.14-3.14C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.07l3.65 2.84C6.7 6.7 9.13 4.75 12 4.75z"/></svg>';
  }

  function cardMarkup(r) {
    var name = escapeHtml(r.name);
    var color = avatarColor(r.name);
    var init = escapeHtml(initials(r.name));
    var rel = escapeHtml(relativeDate(r.createTime));
    var comment = escapeHtml(r.comment);

    var avatar;
    if (r.photo) {
      avatar = '<img class="google-reviews__avatar google-reviews__avatar--img" src="' + escapeHtml(r.photo) +
        '" alt="" loading="lazy" width="40" height="40" referrerpolicy="no-referrer" ' +
        'onerror="this.outerHTML=\'<span class=&quot;google-reviews__avatar&quot; style=&quot;background:' + color + '&quot;>' + init + '</span>\'">';
    } else {
      avatar = '<span class="google-reviews__avatar" style="background:' + color + '">' + init + '</span>';
    }

    return '<article class="google-reviews__card" tabindex="-1">' +
        '<div class="google-reviews__card-top">' +
          starsMarkup(r.rating) +
          googleGlyph() +
        '</div>' +
        '<p class="google-reviews__text">' + comment + '</p>' +
        '<footer class="google-reviews__foot">' +
          avatar +
          '<div class="google-reviews__meta">' +
            '<span class="google-reviews__name">' + name + '</span>' +
            '<span class="google-reviews__date">' + rel + '</span>' +
          '</div>' +
          (r.location ? '<span class="google-reviews__chip">' + escapeHtml(r.location) + '</span>' : '') +
        '</footer>' +
      '</article>';
  }

  var GoogleReviews = function () {};

  if (typeof HTMLElement === 'function') {
    GoogleReviews = function () {
      return Reflect.construct(HTMLElement, [], GoogleReviews);
    };
    GoogleReviews.prototype = Object.create(HTMLElement.prototype);
    GoogleReviews.prototype.constructor = GoogleReviews;
    Object.setPrototypeOf(GoogleReviews, HTMLElement);
  }

  GoogleReviews.prototype.connectedCallback = function () {
    this.grid = this.querySelector('[data-reviews-grid]');
    this.emptyEl = this.querySelector('[data-reviews-empty]');
    this.bindKeyboard();
    if (this._booted) return;
    this._booted = true;
    this.run();
  };

  GoogleReviews.prototype.bindKeyboard = function () {
    if (!this.grid || this.grid._googleReviewsKeyboard) return;

    var root = this;
    root.statusEl = root.querySelector('[data-carousel-status]');
    if (!root.statusEl) {
      root.statusEl = document.createElement('div');
      root.statusEl.className = 'visually-hidden';
      root.statusEl.setAttribute('data-carousel-status', '');
      root.statusEl.setAttribute('aria-live', 'polite');
      root.statusEl.setAttribute('aria-atomic', 'true');
      root.appendChild(root.statusEl);
    }

    this.grid._googleReviewsKeyboard = true;
    var handler = function (event) {
      var keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'];
      if (keys.indexOf(event.key) === -1) return;

      var cards = Array.prototype.slice.call(
        this.querySelectorAll('.google-reviews__card:not(.google-reviews__card--skeleton)')
      );
      if (!cards.length) return;

      event.preventDefault();

      var activeIndex = cards.indexOf(document.activeElement);
      if (activeIndex < 0) {
        var gridLeft = this.getBoundingClientRect().left;
        var closestDistance = Infinity;
        activeIndex = 0;

        cards.forEach(function (card, index) {
          var distance = Math.abs(card.getBoundingClientRect().left - gridLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            activeIndex = index;
          }
        });
      }

      var targetIndex = activeIndex;
      var cardWidth = cards[0].getBoundingClientRect().width || 1;
      var pageStep = Math.max(1, Math.floor(this.clientWidth / cardWidth));
      if (event.key === 'ArrowRight') targetIndex += 1;
      if (event.key === 'ArrowLeft') targetIndex -= 1;
      if (event.key === 'PageDown') targetIndex += pageStep;
      if (event.key === 'PageUp') targetIndex -= pageStep;
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = cards.length - 1;
      targetIndex = Math.max(0, Math.min(cards.length - 1, targetIndex));

      var target = cards[targetIndex];
      if (!target) return;
      root.statusEl.textContent = 'Review ' + (targetIndex + 1) + ' of ' + cards.length;
      try {
        target.focus({ preventScroll: true });
      } catch (err) {
        target.focus();
      }
      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    };

    this.grid._googleReviewsKeyboardHandler = handler;
    this.grid.addEventListener('keydown', handler);
  };

  GoogleReviews.prototype.disconnectedCallback = function () {
    if (!this.grid || !this.grid._googleReviewsKeyboardHandler) return;

    this.grid.removeEventListener('keydown', this.grid._googleReviewsKeyboardHandler);
    delete this.grid._googleReviewsKeyboard;
    delete this.grid._googleReviewsKeyboardHandler;
  };

  GoogleReviews.prototype.config = function () {
    return {
      feed1: (this.getAttribute('data-feed-id-1') || '').trim(),
      feed2: (this.getAttribute('data-feed-id-2') || '').trim(),
      base: (this.getAttribute('data-api-base-url') || 'https://featurable.com/api/v1/widgets').trim().replace(/\/+$/, ''),
      label1: (this.getAttribute('data-label-1') || 'Gepps Cross').trim(),
      label2: (this.getAttribute('data-label-2') || 'Mile End').trim(),
      minRating: num(this.getAttribute('data-min-rating'), 4),
      maxReviews: num(this.getAttribute('data-max-reviews'), 6),
      useSample: this.getAttribute('data-use-sample') === 'true'
    };
  };

  GoogleReviews.prototype.run = function () {
    var cfg = this.config();
    var self = this;

    if (cfg.useSample || (!cfg.feed1 && !cfg.feed2)) {
      this.finish(this.process(sampleReviews(cfg.label1, cfg.label2), cfg));
      return;
    }

    var jobs = [];
    if (cfg.feed1) jobs.push(this.fetchFeed(cfg.base, cfg.feed1, cfg.label1));
    if (cfg.feed2) jobs.push(this.fetchFeed(cfg.base, cfg.feed2, cfg.label2));

    Promise.allSettled(jobs).then(function (results) {
      var all = [];
      var hadSuccess = false;
      results.forEach(function (res) {
        if (res.status === 'fulfilled' && Array.isArray(res.value)) {
          hadSuccess = true;
          all = all.concat(res.value);
        }
      });

      var processed = self.process(all, cfg);
      if (!hadSuccess || !processed.length) {
        // Never leave the homepage broken/empty — fall back to sample data.
        console.warn('[google-reviews] live feed unavailable or empty, using sample reviews.');
        processed = self.process(sampleReviews(cfg.label1, cfg.label2), cfg);
      }
      self.finish(processed);
    });
  };

  GoogleReviews.prototype.fetchFeed = function (base, feedId, label) {
    var url = base + '/' + encodeURIComponent(feedId);
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (resp) {
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        return resp.json();
      })
      .then(function (data) {
        var list = (data && Array.isArray(data.reviews)) ? data.reviews : [];
        return list
          .map(function (raw) { return normalise(raw, label); })
          .filter(Boolean);
      })
      .catch(function (err) {
        console.warn('[google-reviews] fetch failed for widget ' + feedId + ':', err && err.message);
        throw err;
      });
  };

  GoogleReviews.prototype.process = function (reviews, cfg) {
    var seen = {};
    return (reviews || [])
      .filter(function (r) { return r && r.rating >= cfg.minRating; })
      .filter(function (r) {
        if (seen[r.reviewId]) return false;
        seen[r.reviewId] = true;
        return true;
      })
      .sort(function (a, b) {
        return (Date.parse(b.createTime) || 0) - (Date.parse(a.createTime) || 0);
      })
      .slice(0, cfg.maxReviews);
  };

  GoogleReviews.prototype.finish = function (reviews) {
    if (!this.grid) return;

    if (!reviews.length) {
      this.grid.innerHTML = '';
      this.grid.hidden = true;
      if (this.emptyEl) this.emptyEl.hidden = false;
      return;
    }

    if (this.emptyEl) this.emptyEl.hidden = true;
    this.grid.hidden = false;
    this.grid.innerHTML = reviews.map(cardMarkup).join('');
    this.grid.classList.add('is-loaded');
  };

  if ('customElements' in window && !customElements.get('google-reviews')) {
    customElements.define('google-reviews', GoogleReviews);
  }

  // Re-init when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    var nodes = e.target.querySelectorAll('google-reviews');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      el._booted = false;
      if (typeof el.connectedCallback === 'function') el.connectedCallback();
    }
  });
})();
